import type { Graphics } from "pixi.js";

export type DebugCategory = "stats" | "collision" | "chunks" | "net";

export interface DebugViewBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface DebugProvider {
  category: DebugCategory;
  /** Display name shown in HUD groupings. */
  name?: string;
  /** Draw world-space (or HUD-space if category is "stats") debug overlay. */
  draw?: (g: Graphics, view: DebugViewBounds) => void;
  /** Lines shown in the textual HUD panel for this provider. */
  lines?: () => string[];
}

/**
 * Pluggable registry of debug providers. Subsystems (chunks, collision,
 * networking) register themselves here; the DebugOverlay reads back active
 * providers per-category to draw and to print stats.
 */
export class DebugRegistry {
  private providers = new Set<DebugProvider>();

  register(provider: DebugProvider): () => void {
    this.providers.add(provider);
    return () => {
      this.providers.delete(provider);
    };
  }

  list(category?: DebugCategory): DebugProvider[] {
    if (!category) return [...this.providers];
    const out: DebugProvider[] = [];
    for (const p of this.providers) {
      if (p.category === category) out.push(p);
    }
    return out;
  }

  clear(): void {
    this.providers.clear();
  }
}
